import { t } from 'i18next';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { COLORS } from 'Components/foundation';
import { VIDEO_PROVIDERS } from 'Components/modal/MVideoUpload/utils';
import SInput from 'Components/structural/SInput/SInput';
import STextArea from 'Components/structural/STextArea/STextArea';
import UDatePicker from 'Components/unit/UDatePicker/UDatePicker';
import { UFieldError } from 'Components/unit/UFieldError/UFieldError';
import UHourStepper from 'Components/unit/UHourStepper/UHourStepper';
import UIcon from 'Components/unit/UIcon/UIcon';
import ULabel from 'Components/unit/ULabel/ULabel';
import UMediaPicker, { ShowableVideo, isShowableVideo } from 'Components/unit/UMediaPicker/UMediaPicker';
import UToggle from 'Components/unit/UToggle/UToggle';
import { useTypedSelector } from 'Libs/redux/utils';
import Enum from 'Models/Enum';
import { BreakingNewsFormValues } from 'Pages/Activity/BreakingNews/BreakingNewsForm.hook';

import styles from './BreakingNewsContentForm.style';

const NAME_CHAR_LIMIT = 40;
const DESCRIPTION_CHAR_LIMIT = 80;
const LONG_TEXT_CHAR_LIMIT = 10000;

export const BreakingNewsContentForm: React.FC = () => {
  const { selectedLanguage } = useTypedSelector((state) => state.entities.activity.edition);

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<BreakingNewsFormValues>();

  const onNewGameMedia = useCallback(
    ({ type, media }) => {
      if (!type || !media) return;

      setValue(`coverMedia.${type === 'video' ? 'video' : 'image'}`, media);
    },
    [setValue],
  );

  return (
    <div style={styles.wrapper}>
      <section style={styles.section}>
        <div style={styles.titleContainer}>
          <UIcon name="breaking-news" size={48} color={COLORS.ACTIVITY.BREAKING_NEWS_PRIMARY} />
          <h2 style={styles.title}>{t('breaking_news:edition.title')}</h2>
        </div>
        <ULabel size="L" style={styles.subtitle}>
          {t('breaking_news:edition.title_content')}
        </ULabel>
        <div style={styles.infosContainer}>
          <div style={styles.imageContainer}>
            <div>
              <UMediaPicker
                dropArea
                size="L"
                image={watch('coverMedia.image')?.url || ''}
                video={
                  isShowableVideo(watch('coverMedia.video')) ? (watch('coverMedia.video') as ShowableVideo) : undefined
                }
                media={{
                  mediaType: 'imageOrVideo',
                  onChange: onNewGameMedia,
                  providers: [VIDEO_PROVIDERS[Enum.videoType.Youtube], VIDEO_PROVIDERS[Enum.videoType.ApiVideo]],
                }}
                onClearMedia={() => setValue('coverMedia', undefined, { shouldDirty: true })}
                style={styles.mediaPicker}
              />
              <UFieldError errorMessage={errors?.coverMedia?.message} />
            </div>
          </div>
          <div style={styles.infosInputContainer}>
            <SInput
              value={watch(`name.${selectedLanguage}`)}
              onChange={(value) => setValue(`name.${selectedLanguage}`, value, { shouldDirty: true })}
              extraInputStyle={styles.inputBackground}
              placeholder={t('breaking_news:edition.name_placeholder')}
              label={t('breaking_news:edition.name')}
              required
              type="large"
              maxLength={NAME_CHAR_LIMIT}
              radiusWrapper={8}
              errorMessage={errors?.name?.message}
              dataTestContext="breaking-news-name"
            />
            <STextArea
              isLabelRequired
              height="65"
              textAreaStyle={styles.inputBackground}
              textAreaTypo="BODY2"
              maxLength={DESCRIPTION_CHAR_LIMIT}
              value={watch(`description.${selectedLanguage}`)}
              onChange={(value) => setValue(`description.${selectedLanguage}`, value, { shouldDirty: true })}
              label={t('breaking_news:edition.description')}
              placeholder={t('breaking_news:edition.description_placeholder')}
              radiusWrapper={8}
              canResize={false}
              style={styles.inputRadius}
              errorMessage={errors?.description?.message}
              dataTestContext="breaking-news-description"
            />
          </div>
        </div>
        <div style={styles.showLongText}>
          <p>{t('breaking_news:edition.toggle_long_text')}</p>
          <UToggle toggled={watch('isLongText')} onChange={() => setValue('isLongText', !watch('isLongText'))} />
        </div>
        {watch('isLongText') && (
          <STextArea
            height="150"
            maxLength={LONG_TEXT_CHAR_LIMIT}
            textAreaTypo="BODY2"
            textAreaStyle={styles.inputBackground}
            value={watch(`text.${selectedLanguage}`)}
            onChange={(value) => setValue(`text.${selectedLanguage}`, value, { shouldDirty: true })}
            placeholder={t('breaking_news:edition.long_text_placeholder')}
            radiusWrapper={8}
            canResize={false}
            errorMessage={errors?.text?.message}
            dataTestContext="breaking-news-long-text"
          />
        )}
      </section>
      <section style={styles.section}>
        <div style={styles.subtitle}>
          <ULabel
            size="L"
            tooltip={{
              tooltipContent: t('breaking_news:edition.settings_title_tooltip'),
              tooltipPosition: 'top',
            }}
          >
            {t('breaking_news:edition.title_settings')}
          </ULabel>
        </div>
        <div>
          <div style={styles.availablePeriodTitle}>
            <ULabel iconSize={16} icon="calendar" iconColor={COLORS.GREY_DARK.DEFAULT} size="M">
              {t('breaking_news:edition.title_available_period')}
            </ULabel>
          </div>
          <div style={styles.datePickerContainer}>
            <p style={styles.datePickerLabel}>{t('breaking_news:edition.date_picker.from')}</p>
            <UDatePicker
              date={watch('startDate') || null}
              style={styles.datePicker}
              onClick={(value: Date) => setValue('startDate', value)}
            />
            <p style={styles.datePickerLabel}>{t('breaking_news:edition.date_picker.to')}</p>
            <UDatePicker
              date={watch('endDate') || null}
              style={styles.datePicker}
              onClick={(value: Date) => setValue('endDate', value)}
            />
          </div>
        </div>
        <span style={styles.separator}></span>
        <div>
          <div style={styles.notificationsTitle}>
            <ULabel iconSize={16} icon="notifications" iconColor={COLORS.GREY_DARK.DEFAULT} size="M">
              {t('breaking_news:edition.title_notification')}
            </ULabel>
            <UToggle
              toggled={watch('isNotificationActive')}
              onChange={() => setValue('isNotificationActive', !watch('isNotificationActive'))}
            />
          </div>
        </div>
        {watch('isNotificationActive') && (
          <div style={styles.notificationContainer}>
            <div style={styles.timeContainer}>
              <p>{t('breaking_news:edition.hour_label')}</p>
              <UHourStepper
                dataTestContext="breaking-news-notification-time"
                interval={15}
                value={watch('notificationTime')}
                onTimeChange={(value: Date) => setValue('notificationTime', value)}
              />
            </div>
            <div style={styles.recurringNotificationContainer}>
              <p>{t('breaking_news:edition.is_recurring_notification_label')}</p>
              <UToggle
                dataTestContext="breaking-news-recurring-notification"
                toggled={watch('isRecurringNotification')}
                onChange={() => setValue('isRecurringNotification', !watch('isRecurringNotification'))}
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
